import React, { forwardRef, useId } from 'react';
import styled from 'styled-components';
import { Form, InputGroup } from 'react-bootstrap';
import Label from './Label';
import { colors, radius, shadow, spacing, transition, typography } from '../tokens';

// TextInput — wrapper Form.Control với label + error + helper. Hỗ trợ
// `leftIcon` / `rightIcon` (đặt trong InputGroup). Bootstrap `isInvalid`
// được map từ prop `error` (string non-empty).
const TextInput = forwardRef(function TextInput(
  {
    label,
    required,
    error,
    helperText,
    leftIcon,
    rightIcon,
    className,
    id,
    ...controlProps
  },
  ref
) {
  const autoId = useId();
  const inputId = id || autoId;

  const control = (
    <StyledControl
      ref={ref}
      id={inputId}
      isInvalid={!!error}
      aria-invalid={!!error || undefined}
      aria-describedby={
        error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
      }
      {...controlProps}
    />
  );

  return (
    <FieldWrap className={className}>
      {label && (
        <Label htmlFor={inputId} required={required}>
          {label}
        </Label>
      )}

      {leftIcon || rightIcon ? (
        <StyledGroup hasValidation>
          {leftIcon && <Addon>{leftIcon}</Addon>}
          {control}
          {rightIcon && <Addon>{rightIcon}</Addon>}
          {error && (
            <Form.Control.Feedback type='invalid' id={`${inputId}-error`}>
              {error}
            </Form.Control.Feedback>
          )}
        </StyledGroup>
      ) : (
        <>
          {control}
          {error && (
            <Form.Control.Feedback type='invalid' id={`${inputId}-error`}>
              {error}
            </Form.Control.Feedback>
          )}
        </>
      )}

      {!error && helperText && (
        <Helper id={`${inputId}-helper`}>{helperText}</Helper>
      )}
    </FieldWrap>
  );
});

const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${spacing[4]};
`;

const StyledControl = styled(Form.Control)`
  font-size: ${typography.size.base};
  color: ${colors.gray900};
  background: ${colors.surface};
  border: 1px solid ${colors.border};
  border-radius: ${radius.md};
  padding: 10px 12px;
  transition: ${transition.fast};

  &::placeholder {
    color: ${colors.gray400};
  }

  &:hover:not(:disabled):not(:focus) {
    border-color: ${colors.borderHover};
  }

  &:focus {
    border-color: ${colors.primary};
    box-shadow: ${shadow.focus};
  }

  &:disabled {
    background: ${colors.gray50};
    color: ${colors.gray500};
    cursor: not-allowed;
  }

  &.is-invalid {
    border-color: ${colors.danger};
    background-image: none; /* tắt icon ! mặc định của Bootstrap, dùng feedback text thôi */
    padding-right: 12px;
  }
  &.is-invalid:focus {
    box-shadow: 0 0 0 3px ${colors.dangerSoft};
  }
`;

const StyledGroup = styled(InputGroup)`
  border-radius: ${radius.md};
  overflow: hidden;

  /* Bo lại border-radius cho control khi nằm trong group */
  > .form-control {
    border-radius: ${radius.md} 0 0 ${radius.md};
  }
  > .form-control:not(:first-child) {
    border-left: 0;
  }
  > .form-control:not(:last-child) {
    border-right: 0;
  }
`;

const Addon = styled(InputGroup.Text)`
  background: ${colors.gray50};
  border: 1px solid ${colors.border};
  color: ${colors.gray500};
  font-size: ${typography.size.base};
`;

const Helper = styled.div`
  margin-top: ${spacing[1]};
  font-size: ${typography.size.xs};
  color: ${colors.gray500};
`;

export default TextInput;
